import { useCallback, useMemo, useState } from 'react';
import { salesManagers } from '../../data/salesManagers';
import { productionTiers } from '../../data/productionTiers';
import { deriveStats, sumMetrics } from '../../utils/metrics';
import { compareRows } from '../../utils/userSort';
import { floorTotals, type FloorRow, type FloorTotals } from '../../utils/floor';
import type { AgentRow, AgentStats, ProductionTier, Team, UserSort } from '../../types/voip';
import type { CallsInsightHubState } from '../CallsInsightHub/useCallsInsightHub';

export type UsersLayout = 'team' | 'list';
export type UsersGrouping = 'team' | 'tier';

export interface RosterGroupData {
  id: string;
  title: string;
  team?: Team;
  tier?: ProductionTier;
  members: number;
  stats: AgentStats;
  floor: FloorTotals;
  prevFloor: FloorTotals;
  /** Users to show (all members, or search matches), already sorted */
  users: AgentRow[];
}

export const PAGE_SIZE = 25;

export function useUsersDirectory(hub: CallsInsightHubState) {
  const [query, setQueryState] = useState('');
  const [sort, setSortState] = useState<UserSort>('talk');
  const [layout, setLayout] = useState<UsersLayout>('team');
  const [grouping, setGroupingState] = useState<UsersGrouping>('team');
  const [expanded, setExpanded] = useState<Set<string>>(() => new Set([salesManagers[0].team]));
  const [collapsedWhileSearching, setCollapsedWhileSearching] = useState<Set<string>>(new Set());
  const [showAll, setShowAll] = useState<Set<string>>(new Set());
  const [page, setPage] = useState(1);

  const q = query.trim().toLowerCase();
  const searching = q.length > 0;

  const setQuery = (value: string) => {
    setQueryState(value);
    setCollapsedWhileSearching(new Set());
    setPage(1);
  };
  const setSort = (value: UserSort) => {
    setSortState(value);
    setPage(1);
  };
  const setGrouping = (value: UsersGrouping) => {
    setGroupingState(value);
    setExpanded(new Set([value === 'team' ? salesManagers[0].team : productionTiers[0].id]));
    setShowAll(new Set());
  };

  const groups = useMemo<RosterGroupData[]>(() => {
    const compare = compareRows(sort, hub.floorById);
    const userMatch = (r: AgentRow) => r.agent.name.toLowerCase().includes(q) || r.agent.extension.includes(q);
    const teamMatches = (team: Team) => {
      const m = salesManagers.find((s) => s.team === team);
      return !!m && (m.label.toLowerCase().includes(q) || m.manager.toLowerCase().includes(q));
    };
    const scoped = hub.team === 'All' ? hub.allRows : hub.allRows.filter((r) => r.agent.team === hub.team);
    const floorOf = (rows: AgentRow[]) =>
      rows.map((r) => hub.floorById.get(r.agent.id)).filter((f): f is FloorRow => !!f);

    const build = (id: string, title: string, members: AgentRow[], extra: Partial<RosterGroupData>): RosterGroupData => {
      const users = !searching ? members : members.filter((r) => userMatch(r) || teamMatches(r.agent.team));
      const floorRows = floorOf(members);
      return {
        id,
        title,
        members: members.length,
        stats: deriveStats(sumMetrics(members.map((r) => r.stats))),
        floor: floorTotals(floorRows, 'day'),
        prevFloor: floorTotals(floorRows, 'prev'),
        users: [...users].sort(compare),
        ...extra,
      };
    };

    const list =
      grouping === 'team'
        ? salesManagers
            .filter((m) => hub.team === 'All' || m.team === hub.team)
            .map((m) => build(m.team, m.label, scoped.filter((r) => r.agent.team === m.team), { team: m.team }))
        : productionTiers.map((t) =>
            build(t.id, t.label, scoped.filter((r) => hub.floorById.get(r.agent.id)?.tier.id === t.id), { tier: t })
          );

    return list.filter((g) => (searching ? g.users.length > 0 : g.members > 0));
  }, [hub.allRows, hub.team, hub.floorById, q, searching, sort, grouping]);

  const matchedIds = useMemo(() => {
    if (!searching) return new Set<string>();
    return new Set(
      hub.allRows
        .filter((r) => r.agent.name.toLowerCase().includes(q) || r.agent.extension.includes(q))
        .map((r) => r.agent.id)
    );
  }, [hub.allRows, q, searching]);

  const isExpanded = useCallback(
    (id: string) => (searching ? !collapsedWhileSearching.has(id) : expanded.has(id)),
    [searching, collapsedWhileSearching, expanded]
  );
  const isShowingAll = useCallback((id: string) => searching || showAll.has(id), [searching, showAll]);

  const toggleIn = (set: Set<string>, id: string) => {
    const next = new Set(set);
    if (next.has(id)) next.delete(id);
    else next.add(id);
    return next;
  };

  const toggleGroup = (id: string) => {
    if (searching) setCollapsedWhileSearching((s) => toggleIn(s, id));
    else setExpanded((s) => toggleIn(s, id));
  };

  const setGroupShowAll = (id: string, value: boolean) =>
    setShowAll((s) => {
      const next = new Set(s);
      if (value) next.add(id);
      else next.delete(id);
      return next;
    });

  const allExpanded = groups.length > 0 && groups.every((g) => isExpanded(g.id));
  const toggleAll = () => {
    if (searching) {
      setCollapsedWhileSearching(allExpanded ? new Set(groups.map((g) => g.id)) : new Set());
    } else {
      setExpanded(allExpanded ? new Set() : new Set(groups.map((g) => g.id)));
    }
  };

  const viewTeam = (team: Team) => {
    hub.setTeam(team);
    setExpanded((s) => new Set(s).add(team));
    setGroupShowAll(team, true);
  };

  // List view: every visible user across groups, paginated
  const listRows = useMemo(
    () => groups.flatMap((g) => g.users).sort(compareRows(sort, hub.floorById)),
    [groups, sort, hub.floorById]
  );
  const pageCount = Math.max(1, Math.ceil(listRows.length / PAGE_SIZE));
  const currentPage = Math.min(page, pageCount);
  const pageRows = listRows.slice((currentPage - 1) * PAGE_SIZE, currentPage * PAGE_SIZE);

  const totalUsers = hub.team === 'All' ? hub.allRows.length : hub.allRows.filter((r) => r.agent.team === hub.team).length;
  const shownUsers = groups.reduce((s, g) => s + g.users.length, 0);

  return {
    query,
    setQuery,
    searching,
    sort,
    setSort,
    layout,
    setLayout,
    grouping,
    setGrouping,
    groups,
    matchedIds,
    isExpanded,
    isShowingAll,
    toggleGroup,
    setGroupShowAll,
    allExpanded,
    toggleAll,
    viewTeam,
    listRows,
    pageRows,
    page: currentPage,
    pageCount,
    setPage,
    totalUsers,
    shownUsers,
  };
}
